import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  TextField,
  Tooltip,
  Typography,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  DragIndicator as DragIndicatorIcon,
  PolicyOutlined as PolicyIcon,
  Refresh as RefreshIcon,
  RestoreOutlined as RestoreIcon,
  FileDownload as FileDownloadIcon,
  FileUpload as FileUploadIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DashboardLayout from '../components/DashboardLayout';
import { useAppDispatch, useAppSelector } from '../store';
import { setSettings, setFieldListForSetting, addSetting, fetchApplicationSettings, fetchUIFields } from '../store/applicationSettingsSlice';
import { apiService } from '../services/api';
import { showNotification } from '../store/notificationSlice';
import type { ApplicationSetting, FieldSetting, FieldSettingType, FieldSize } from '../types/applicationSettings';

// ─── Size slider helpers ──────────────────────────────────────────────────────

const sizeMarks = [
  { value: 0 },
  { value: 1 },
  { value: 2 },
  { value: 3 },
];
const sizeToValue: Record<FieldSize, number> = { '1': 0, '2': 1, '3': 2, '4': 3 };
const valueToSize: Record<number, FieldSize> = { 0: '1', 1: '2', 2: '3', 3: '4' };

// ─── Dirty detection ──────────────────────────────────────────────────────────

function isFieldDirty(current: FieldSetting, original: FieldSetting | undefined): boolean {
  if (!original) return false;
  return (
    current.active !== original.active ||
    current.required !== original.required ||
    !!current.newline !== !!original.newline ||
    (sizeToValue[current.size] ?? 0) !== (sizeToValue[original.size] ?? 0) ||
    (current.pattern ?? '') !== (original.pattern ?? '') ||
    (current.errorMessage ?? '') !== (original.errorMessage ?? '')
  );
}

// ─── Type chip helper ─────────────────────────────────────────────────────────

function typeChipFor(type: FieldSetting['type']) {
  if (type === 'tag') return { label: 'Tag', color: '#e3f2fd', textColor: '#1565c0' };
  if (type === 'fixed_field') return { label: 'Default Field', color: '#f3e5f5', textColor: '#6a1b9a' };
  return { label: 'Custom Field', color: '#e8f5e9', textColor: '#2e7d32' };
}

// ─── Draggable row ────────────────────────────────────────────────────────────

interface DraggableRowProps {
  field: FieldSetting;
  originalField: FieldSetting | undefined;
  onFieldChange: (name: string, changes: Partial<FieldSetting>) => void;
  onReset: (name: string) => void;
  onOpenValidation: (field: FieldSetting) => void;
}

function DraggableRow({ field, originalField, onFieldChange, onReset, onOpenValidation }: DraggableRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: field.name });

  const dirty = isFieldDirty(field, originalField);
  const chip = typeChipFor(field.type);
  const hasValidation = !!(field.pattern || field.errorMessage);

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleActiveChange = (checked: boolean) => {
    onFieldChange(field.name, { active: checked, required: checked ? field.required : false });
  };

  const handleRequiredChange = (checked: boolean) => {
    onFieldChange(field.name, { required: checked });
  };

  const handleSizeChange = (_: Event, value: number | number[]) => {
    onFieldChange(field.name, { size: valueToSize[value as number] });
  };

  const stopPropagation = (e: React.PointerEvent | React.MouseEvent) => e.stopPropagation();

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...attributes}
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: dirty ? 'rgba(255, 193, 7, 0.07)' : 'background.paper',
        '&:last-child': { borderBottom: 'none' },
        transition: 'background-color 0.2s',
      }}
    >
      {/* Drag handle — hidden for pinned fields */}
      <Box
        {...(field.pinned ? {} : listeners)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1,
          cursor: field.pinned ? 'default' : isDragging ? 'grabbing' : 'grab',
          color: field.pinned ? 'transparent' : 'text.disabled',
          flexShrink: 0,
        }}
      >
        <DragIndicatorIcon fontSize="small" />
      </Box>

      {/* Main content */}
      <Box sx={{ flex: 1, py: 1.5, pr: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {/* Left: name + chip */}
        <Typography variant="body2" fontWeight={500} sx={{ flexShrink: 0 }}>
          {field.name}
        </Typography>
        <Chip
          label={chip.label}
          size="small"
          sx={{ height: 18, fontSize: '0.65rem', bgcolor: chip.color, color: chip.textColor, fontWeight: 600, flexShrink: 0 }}
        />

        <Box sx={{ flex: 1 }} />

        {/* Right: size + checkboxes + icons — grouped so they wrap as one unit */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexShrink: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mt: '4px' }}>
            Size
          </Typography>
          <Slider
            value={sizeToValue[field.size] ?? 0}
            onChange={handleSizeChange}
            onPointerDown={stopPropagation}
            min={0}
            max={3}
            step={1}
            marks={sizeMarks}
            size="small"
            sx={{ width: 80, ml: 1, mr: 3 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={field.active}
                onChange={(e) => handleActiveChange(e.target.checked)}
                onPointerDown={stopPropagation}
                disabled={field.alwaysActive}
                sx={{ p: 0.5 }}
              />
            }
            label={<Typography variant="caption">Active</Typography>}
            sx={{ m: 0, mr: 0.5 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={field.required}
                onChange={(e) => handleRequiredChange(e.target.checked)}
                disabled={!field.active || field.alwaysRequired}
                onPointerDown={stopPropagation}
                sx={{ p: 0.5 }}
              />
            }
            label={<Typography variant="caption">Required</Typography>}
            sx={{ m: 0, mr: 0.5 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={!!field.newline}
                onChange={(e) => onFieldChange(field.name, { newline: e.target.checked })}
                disabled={!field.active}
                onPointerDown={stopPropagation}
                sx={{ p: 0.5 }}
              />
            }
            label={<Typography variant="caption">New Line</Typography>}
            sx={{ m: 0, mr: 0.5 }}
          />
          <Tooltip title={hasValidation ? 'Validation rule set' : 'Set validation rule'}>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onOpenValidation(field); }}
              onPointerDown={stopPropagation}
              sx={{ color: hasValidation ? 'error.main' : 'text.secondary' }}
            >
              <PolicyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reset to saved state">
            <span>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onReset(field.name); }}
                onPointerDown={stopPropagation}
                disabled={!dirty}
                sx={{ color: dirty ? 'warning.main' : 'text.disabled' }}
              >
                <RestoreIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}

// ─── New Profile Dialog ───────────────────────────────────────────────────────

const SYSTEM_DEFAULT_VALUE = '__system_default__';

interface NewProfileDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (setting: ApplicationSetting, copyFromId: string) => void;
  settings: ApplicationSetting[];
  defaultCopyFromId: string;
}

function NewProfileDialog({ open, onClose, onCreated, settings, defaultCopyFromId }: NewProfileDialogProps) {
  const [name, setName] = useState('');
  const [copyFromId, setCopyFromId] = useState(SYSTEM_DEFAULT_VALUE);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setName('');
      setError('');
      setSubmitting(false);
      setCopyFromId(defaultCopyFromId || SYSTEM_DEFAULT_VALUE);
    }
  }, [open, defaultCopyFromId]);

  const handleAdd = async () => {
    setError('');
    setSubmitting(true);
    try {
      const sourceId = copyFromId === SYSTEM_DEFAULT_VALUE ? '' : copyFromId;
      const sourceSetting = settings.find((s) => s.settingId === sourceId);
      const payload: { settingName: string; active: boolean; settingJsonString?: string } = {
        settingName: name,
        active: false,
        ...(sourceSetting?.settingJsonString ? (() => {
        try {
          const blob = JSON.parse(sourceSetting.settingJsonString);
          delete blob.historyTrackingUnit;
          return { settingJsonString: JSON.stringify(blob) };
        } catch {
          return {};
        }
      })() : {}),
      };
      const response = await apiService.createApplicationSetting(payload);
      onCreated(response.data, sourceId);
      onClose();
    } catch (err: any) {
      setError(err?.jsonMessage?.settingName || err?.message || 'Failed to create profile');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>New Setting Profile</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
          <TextField
            label="Profile Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) handleAdd(); }}
          />
          <FormControl fullWidth size="small">
            <InputLabel>Copy From</InputLabel>
            <Select
              value={copyFromId}
              label="Copy From"
              onChange={(e) => setCopyFromId(e.target.value)}
            >
              <MenuItem value={SYSTEM_DEFAULT_VALUE}>System Default</MenuItem>
              {settings.map((s) => (
                <MenuItem key={s.settingId} value={s.settingId}>
                  {s.settingName}{s.active ? ' (Active)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={handleAdd} disabled={submitting || !name.trim()}>
          {submitting ? <CircularProgress size={18} /> : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Validation Dialog ────────────────────────────────────────────────────────

interface ValidationDialogProps {
  open: boolean;
  field: FieldSetting | null;
  onClose: () => void;
  onSave: (name: string, pattern: string, errorMessage: string) => void;
}

function ValidationDialog({ open, field, onClose, onSave }: ValidationDialogProps) {
  const [pattern, setPattern] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (open && field) {
      setPattern(field.pattern ?? '');
      setErrorMessage(field.errorMessage ?? '');
    }
  }, [open, field]);

  const handleSave = () => {
    if (!field) return;
    onSave(field.name, pattern, errorMessage);
    onClose();
  };

  if (!field) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Validation Rule — {field.name}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Regex Pattern"
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            fullWidth
            size="small"
            placeholder="e.g. ^[A-Z]+$"
            autoFocus
          />
          <TextField
            label="Error Message"
            value={errorMessage}
            onChange={(e) => setErrorMessage(e.target.value)}
            fullWidth
            size="small"
            placeholder="e.g. Must be uppercase letters only"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Import Dialog ──────────────────────────────────────────────────────────

interface ImportCandidateField {
  name: string;
  type: FieldSettingType;
  size: FieldSize;
  active: boolean;
  required: boolean;
  newline: boolean;
  pattern?: string;
  errorMessage?: string;
  preventInvalidInput: boolean;
  selected: boolean;
  targetField: FieldSetting;
}

interface IncompatibleField {
  name: string;
  type?: string;
  reason: string;
}

interface ImportEmployeeSettingsDialogProps {
  open: boolean;
  fileName: string;
  candidates: ImportCandidateField[];
  incompatible: IncompatibleField[];
  importedCount: number;
  status: 'preview' | 'importing' | 'completed';
  onClose: () => void;
  onToggleSelect: (index: number) => void;
  onToggleSelectAll: () => void;
  onImport: () => void;
}

function ImportEmployeeSettingsDialog({
  open,
  fileName,
  candidates,
  incompatible,
  importedCount,
  status,
  onClose,
  onToggleSelect,
  onToggleSelectAll,
  onImport,
}: ImportEmployeeSettingsDialogProps) {
  const selectedCount = candidates.filter((c) => c.selected).length;
  const allSelected = candidates.length > 0 && selectedCount === candidates.length;
  const someSelected = selectedCount > 0 && selectedCount < candidates.length;

  return (
    <Dialog
      open={open}
      onClose={() => status !== 'importing' && onClose()}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FileUploadIcon />
          <span>Import Employee Entry Settings</span>
        </Box>
        {fileName && (
          <Typography variant="caption" color="text.secondary">
            {fileName}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {status === 'preview' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip label={`Total in File: ${candidates.length + incompatible.length}`} size="small" />
              <Chip
                label={`Compatible: ${candidates.length}`}
                size="small"
                color={candidates.length > 0 ? 'success' : 'default'}
              />
              <Chip
                label={`Selected: ${selectedCount} of ${candidates.length}`}
                size="small"
                color={selectedCount > 0 ? 'primary' : 'default'}
              />
              {incompatible.length > 0 && (
                <Chip
                  label={`Incompatible (Skipped): ${incompatible.length}`}
                  size="small"
                  color="warning"
                />
              )}
            </Box>

            {incompatible.length > 0 && (
              <Alert severity="warning" sx={{ py: 0.5 }}>
                <Typography variant="body2" fontWeight={600}>
                  The following {incompatible.length} field(s)/tag(s) are not configured in this unit and will be skipped:
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 150 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {incompatible.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 500 }}>
                            {item.name}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8125rem' }}>{item.type || 'Unknown'}</TableCell>
                          <TableCell sx={{ fontSize: '0.8125rem', color: 'warning.dark' }}>{item.reason}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Alert>
            )}

            {candidates.length > 0 ? (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Compatible Fields to Import ({selectedCount} selected of {candidates.length})
                  </Typography>
                  <Button size="small" onClick={onToggleSelectAll}>
                    {allSelected ? 'Deselect All' : 'Select All'}
                  </Button>
                </Box>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={allSelected}
                            indeterminate={someSelected}
                            onChange={onToggleSelectAll}
                            inputProps={{ 'aria-label': 'select all fields' }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Field Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Size</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">Active</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">Required</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">New Line</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Validation</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {candidates.map((item, idx) => {
                        const chip = typeChipFor(item.type);
                        return (
                          <TableRow
                            key={idx}
                            hover
                            onClick={() => onToggleSelect(idx)}
                            sx={{ cursor: 'pointer', opacity: item.selected ? 1 : 0.6 }}
                          >
                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                size="small"
                                checked={item.selected}
                                onChange={() => onToggleSelect(idx)}
                              />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 500, fontSize: '0.8125rem', color: item.selected ? 'primary.main' : 'text.secondary' }}>
                              {item.name}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={chip.label}
                                size="small"
                                sx={{ height: 18, fontSize: '0.65rem', bgcolor: chip.color, color: chip.textColor, fontWeight: 600 }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.8125rem' }}>
                              Size {item.size}
                            </TableCell>
                            <TableCell align="center">
                              {item.active ? (
                                <Chip label="Yes" size="small" color="success" variant="outlined" sx={{ height: 20 }} />
                              ) : (
                                <Chip label="No" size="small" variant="outlined" sx={{ height: 20 }} />
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {item.required ? (
                                <Chip label="Yes" size="small" color="error" variant="outlined" sx={{ height: 20 }} />
                              ) : (
                                <Typography variant="caption" color="text.secondary">—</Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {item.newline ? (
                                <Chip label="Yes" size="small" color="info" variant="outlined" sx={{ height: 20 }} />
                              ) : (
                                <Typography variant="caption" color="text.secondary">—</Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {item.pattern ? (
                                <Tooltip title={`${item.pattern}${item.errorMessage ? ' (' + item.errorMessage + ')' : ''}`}>
                                  <span>{item.pattern}</span>
                                </Tooltip>
                              ) : (
                                '—'
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ) : (
              <Alert severity="info">
                No compatible fields found in this file to import.
              </Alert>
            )}
          </Box>
        )}

        {status === 'importing' && (
          <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={36} />
            <Typography variant="body1" fontWeight={500}>
              Applying employee settings...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Saving merged field configuration to the current profile.
            </Typography>
            <Box sx={{ width: '100%', maxWidth: 400 }}>
              <LinearProgress />
            </Box>
          </Box>
        )}

        {status === 'completed' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="success">
              <Typography variant="body2" fontWeight={600}>
                Successfully imported settings for {importedCount} field(s)!
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Field ordering, active states, requirements, sizes, and validation rules have been applied.
              </Typography>
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {status === 'preview' && (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={onImport}
              disabled={selectedCount === 0}
              startIcon={<FileUploadIcon />}
            >
              Import Selected ({selectedCount})
            </Button>
          </>
        )}
        {status === 'importing' && (
          <Button disabled>Importing...</Button>
        )}
        {status === 'completed' && (
          <Button variant="contained" onClick={onClose}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ApplicationSettings() {
  const dispatch = useAppDispatch();
  const { settings, fieldLists, isLoading, error } = useAppSelector((state) => state.applicationSettings);

  const [selectedSettingId, setSelectedSettingId] = useState<string>('');
  const [fieldList, setFieldList] = useState<FieldSetting[]>([]);
  const [loadingFields, setLoadingFields] = useState(false);

  const [newProfileOpen, setNewProfileOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [validationField, setValidationField] = useState<FieldSetting | null>(null);
  const [validationOpen, setValidationOpen] = useState(false);

  // Import / Export state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFileName, setImportFileName] = useState('');
  const [candidatesToImport, setCandidatesToImport] = useState<ImportCandidateField[]>([]);
  const [incompatibleItems, setIncompatibleItems] = useState<IncompatibleField[]>([]);
  const [importStatus, setImportStatus] = useState<'preview' | 'importing' | 'completed'>('preview');
  const [importedCount, setImportedCount] = useState(0);

  const handleExport = () => {
    if (fieldList.length === 0) {
      dispatch(showNotification({ message: 'No field settings to export', severity: 'warning' }));
      return;
    }

    const currentSetting = settings.find((s) => s.settingId === selectedSettingId);
    const profileName = currentSetting?.settingName || 'Employee Entry Settings';

    const exportData = {
      settingName: profileName,
      exportedAt: new Date().toISOString(),
      fieldSettingList: fieldList.map((f) => ({
        serial: f.serial,
        name: f.name,
        type: f.type,
        size: f.size,
        active: f.active,
        required: f.required,
        newline: Boolean(f.newline),
        pattern: f.pattern || undefined,
        errorMessage: f.errorMessage || undefined,
        preventInvalidInput: f.preventInvalidInput,
        pinned: f.pinned,
        alwaysActive: f.alwaysActive,
        alwaysRequired: f.alwaysRequired,
      })),
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const sanitizedName = profileName.toLowerCase().replace(/[^a-z0-9_-]/g, '_');
    link.download = `employee-settings-${sanitizedName}-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    dispatch(
      showNotification({
        message: `Exported ${fieldList.length} field setting(s) for "${profileName}" successfully`,
        severity: 'success',
      })
    );
  };

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImportFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target?.result as string;
        const parsed = JSON.parse(text);

        let incomingList: any[] | null = null;
        if (Array.isArray(parsed)) {
          incomingList = parsed;
        } else if (Array.isArray(parsed?.fieldSettingList)) {
          incomingList = parsed.fieldSettingList;
        } else if (Array.isArray(parsed?.fieldList)) {
          incomingList = parsed.fieldList;
        } else if (Array.isArray(parsed?.fields)) {
          incomingList = parsed.fields;
        } else if (typeof parsed?.settingJsonString === 'string') {
          try {
            const blob = JSON.parse(parsed.settingJsonString);
            if (Array.isArray(blob?.fieldSettingList)) {
              incomingList = blob.fieldSettingList;
            }
          } catch {
            // ignore
          }
        }

        if (!incomingList || incomingList.length === 0) {
          dispatch(
            showNotification({
              message: 'Invalid JSON file: Expected an array or profile object with field settings.',
              severity: 'error',
            })
          );
          return;
        }

        const currentByName = new Map(fieldList.map((f) => [f.name.toLowerCase(), f]));

        const candidates: ImportCandidateField[] = [];
        const incompatible: IncompatibleField[] = [];
        const seenInFile = new Set<string>();

        for (const raw of incomingList) {
          if (!raw || typeof raw !== 'object') continue;
          const rawName = String(raw.name || '').trim();
          if (!rawName) {
            incompatible.push({
              name: 'Unknown',
              reason: 'Missing field name',
            });
            continue;
          }

          const lowerName = rawName.toLowerCase();
          if (seenInFile.has(lowerName)) {
            incompatible.push({
              name: rawName,
              type: raw.type,
              reason: 'Duplicate entry in imported file',
            });
            continue;
          }
          seenInFile.add(lowerName);

          const target = currentByName.get(lowerName);
          if (!target) {
            const typeStr = raw.type === 'tag' ? 'Tag' : raw.type === 'field' ? 'Custom Field' : 'Field';
            incompatible.push({
              name: rawName,
              type: raw.type,
              reason: `${typeStr} "${rawName}" is not configured in this unit`,
            });
            continue;
          }

          let sizeVal: FieldSize = target.size;
          const rawSize = String(raw.size ?? '');
          if (['1', '2', '3', '4'].includes(rawSize)) {
            sizeVal = rawSize as FieldSize;
          }

          let activeVal = Boolean(raw.active ?? target.active);
          if (target.alwaysActive) {
            activeVal = true;
          }

          let reqVal = Boolean(raw.required ?? target.required);
          if (target.alwaysRequired) {
            reqVal = true;
          }
          if (!activeVal) {
            reqVal = false;
          }

          candidates.push({
            name: target.name,
            type: target.type,
            size: sizeVal,
            active: activeVal,
            required: reqVal,
            newline: Boolean(raw.newline),
            pattern: typeof raw.pattern === 'string' ? raw.pattern : target.pattern,
            errorMessage: typeof raw.errorMessage === 'string' ? raw.errorMessage : target.errorMessage,
            preventInvalidInput: Boolean(raw.preventInvalidInput ?? (raw.pattern || raw.errorMessage)),
            selected: true,
            targetField: target,
          });
        }

        setCandidatesToImport(candidates);
        setIncompatibleItems(incompatible);
        setImportDialogOpen(true);
        setImportStatus('preview');
      } catch (err: any) {
        dispatch(
          showNotification({
            message: 'Failed to parse JSON file: ' + (err?.message || 'Invalid JSON syntax'),
            severity: 'error',
          })
        );
      } finally {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    };
    reader.readAsText(file);
  };

  const handleToggleSelect = (index: number) => {
    setCandidatesToImport((prev) =>
      prev.map((item, idx) => (idx === index ? { ...item, selected: !item.selected } : item))
    );
  };

  const handleToggleSelectAll = () => {
    setCandidatesToImport((prev) => {
      const allSelected = prev.every((c) => c.selected);
      return prev.map((item) => ({ ...item, selected: !allSelected }));
    });
  };

  const handleExecuteImport = async () => {
    setImportStatus('importing');
    const selected = candidatesToImport.filter((c) => c.selected);
    const selectedByName = new Map(selected.map((c) => [c.name.toLowerCase(), c]));

    const pinnedFields = fieldList.filter((f) => f.pinned);
    const pinnedNames = new Set(pinnedFields.map((f) => f.name.toLowerCase()));

    const importedOrderNonPinned: FieldSetting[] = [];
    for (const c of selected) {
      if (pinnedNames.has(c.name.toLowerCase())) continue;
      importedOrderNonPinned.push({
        ...c.targetField,
        size: c.size,
        active: c.active,
        required: c.required,
        newline: c.newline,
        pattern: c.pattern,
        errorMessage: c.errorMessage,
        preventInvalidInput: c.preventInvalidInput,
      });
    }

    const updatedPinned: FieldSetting[] = pinnedFields.map((f) => {
      const match = selectedByName.get(f.name.toLowerCase());
      if (match) {
        return {
          ...f,
          size: match.size,
          active: f.alwaysActive ? true : match.active,
          required: f.alwaysRequired ? true : match.required,
          newline: match.newline,
          pattern: match.pattern,
          errorMessage: match.errorMessage,
          preventInvalidInput: match.preventInvalidInput,
        };
      }
      return f;
    });

    const includedNames = new Set([
      ...updatedPinned.map((f) => f.name.toLowerCase()),
      ...importedOrderNonPinned.map((f) => f.name.toLowerCase()),
    ]);
    const remainingFields = fieldList.filter((f) => !includedNames.has(f.name.toLowerCase()));

    const combinedList = [...updatedPinned, ...importedOrderNonPinned, ...remainingFields];
    const withSerials = combinedList.map((f, idx) => ({ ...f, serial: idx + 1 }));

    try {
      await apiService.saveFieldList(selectedSettingId, withSerials);
      dispatch(setFieldListForSetting({ settingId: selectedSettingId, fieldList: withSerials }));
      setFieldList(withSerials);
      dispatch(fetchUIFields());
      setImportedCount(selected.length);
      setImportStatus('completed');
      dispatch(
        showNotification({
          message: `Successfully imported settings for ${selected.length} field(s).`,
          severity: 'success',
        })
      );
    } catch (err: any) {
      dispatch(
        showNotification({
          message: err?.message || 'Failed to save imported settings',
          severity: 'error',
        })
      );
      setImportStatus('preview');
    }
  };

  // [TEMP] UI Fields debug modal
  const [uiFieldsOpen, setUiFieldsOpen] = useState(false);
  const [uiFieldsData, setUiFieldsData] = useState<any>(null);
  const [uiFieldsLoading, setUiFieldsLoading] = useState(false);
  const handleOpenUiFields = async () => {
    setUiFieldsOpen(true);
    setUiFieldsLoading(true);
    try {
      const res = await apiService.getUIFields();
      setUiFieldsData(res.data);
    } catch (err: any) {
      setUiFieldsData({ error: err?.message ?? 'Failed to load' });
    } finally {
      setUiFieldsLoading(false);
    }
  };

  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Sync selectedSettingId and fieldList when settings/fieldLists change
  useEffect(() => {
    if (settings.length === 0) return;
    const target = settings.find((s) => s.active) ?? settings[0];
    setSelectedSettingId(target.settingId);
    setFieldList(fieldLists[target.settingId] ?? []);
  }, [settings]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch merged/hydrated field list from backend dynamically on change
  useEffect(() => {
    if (!selectedSettingId) return;
    let cancelled = false;
    setLoadingFields(true);
    apiService.getFieldList(selectedSettingId)
      .then((res) => {
        if (!cancelled) {
          setFieldList(res.data ?? []);
        }
      })
      .catch((err: any) => {
        dispatch(showNotification({ message: err?.message ?? 'Failed to load fields', severity: 'error' }));
      })
      .finally(() => {
        if (!cancelled) setLoadingFields(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedSettingId, dispatch]);

  const handleProfileChange = (settingId: string) => {
    setSelectedSettingId(settingId);
    setFieldList(fieldLists[settingId] ?? []);
  };

  const handleFieldChange = (name: string, changes: Partial<FieldSetting>) => {
    setFieldList((prev) => prev.map((f) => (f.name === name ? { ...f, ...changes } : f)));
  };

  const handleItemReset = (name: string) => {
    const original = (fieldLists[selectedSettingId] ?? []).find((f) => f.name === name);
    if (original) setFieldList((prev) => prev.map((f) => (f.name === name ? { ...original } : f)));
  };

  const handleOpenValidation = (field: FieldSetting) => {
    setValidationField(field);
    setValidationOpen(true);
  };

  const handleSaveValidation = (name: string, pattern: string, errorMessage: string) => {
    setFieldList((prev) =>
      prev.map((f) =>
        f.name === name
          ? {
              ...f,
              pattern: pattern || undefined,
              errorMessage: errorMessage || undefined,
              preventInvalidInput: !!(pattern || errorMessage),
            }
          : f
      )
    );
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setFieldList((items) => {
        const oldIndex = items.findIndex((f) => f.name === active.id);
        const newIndex = items.findIndex((f) => f.name === over.id);
        const pinnedCount = items.filter((f) => f.pinned).length;
        // Block: cannot drag a pinned field, cannot drag anything above pinned fields
        if (items[oldIndex].pinned || newIndex < pinnedCount) return items;
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleApply = async () => {
    if (!selectedSettingId) return;
    setSaving(true);
    try {
      const withSerials = fieldList.map((f, idx) => ({ ...f, serial: idx + 1 }));
      const [, activateRes] = await Promise.all([
        apiService.saveFieldList(selectedSettingId, withSerials),
        apiService.updateApplicationSetting(selectedSettingId, { active: true }),
      ]);
      dispatch(setFieldListForSetting({ settingId: selectedSettingId, fieldList: withSerials }));
      // Deactivate all others, activate selected
      dispatch(setSettings(
        settings.map((s) =>
          s.settingId === selectedSettingId
            ? activateRes.data
            : { ...s, active: false }
        )
      ));
      setFieldList(withSerials);
      dispatch(fetchUIFields());
      dispatch(showNotification({ message: 'Profile activated and field list saved.', severity: 'success' }));
    } catch (err: any) {
      dispatch(showNotification({ message: err?.message ?? 'Failed to apply settings', severity: 'error' }));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setFieldList(fieldLists[selectedSettingId] ?? []);
  };

  const handleDelete = async () => {
    if (!selectedSettingId) return;
    setDeleting(true);
    try {
      await apiService.deleteApplicationSetting(selectedSettingId);
      setDeleteConfirmOpen(false);
      // Backend always activates another profile — reflect that in Redux
      const remaining = settings.filter((s) => s.settingId !== selectedSettingId);
      const next = remaining.find((s) => s.active) ?? remaining[0];
      if (next) {
        setSelectedSettingId(next.settingId);
        setFieldList(fieldLists[next.settingId] ?? []);
        dispatch(setSettings(remaining.map((s) => ({ ...s, active: s.settingId === next.settingId }))));
      } else {
        setSelectedSettingId('');
        setFieldList([]);
        dispatch(setSettings([]));
      }
      dispatch(showNotification({ message: 'Profile deleted.', severity: 'success' }));
    } catch (err: any) {
      dispatch(showNotification({ message: err?.message ?? 'Failed to delete profile', severity: 'error' }));
    } finally {
      setDeleting(false);
    }
  };

  const originalMap = Object.fromEntries((fieldLists[selectedSettingId] ?? []).map((f) => [f.name, f]));

  return (
    <DashboardLayout>
      {/* Outer: flex column filling the content area, escaping DashboardLayout's p:3 */}
      <Box sx={{ display: 'flex', flexDirection: 'column', m: -3, minHeight: 'calc(100vh - 64px)' }}>
      {/* Scrollable content with padding restored */}
      <Box sx={{ flexGrow: 1, p: 3, maxWidth: 900 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Typography variant="h5" fontWeight={600}>
            Employee Entry Settings
          </Typography>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={() => dispatch(fetchApplicationSettings())} disabled={isLoading} sx={{ mt: '2px' }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExport}
            disabled={isLoading || fieldList.length === 0}
          >
            Export
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<FileUploadIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading || !selectedSettingId}
          >
            Import
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelected}
            accept=".json,application/json"
            style={{ display: 'none' }}
          />
          {/* [TEMP] debug button */}
          <Button size="small" variant="outlined" onClick={handleOpenUiFields}>
            UI Fields JSON
          </Button>
        </Box>

        {/* Section 1 — Profile Selector Bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <FormControl sx={{ flex: 1 }} size="small" disabled={isLoading}>
            <InputLabel>Setting Profile</InputLabel>
            <Select
              value={selectedSettingId}
              label="Setting Profile"
              onChange={(e) => handleProfileChange(e.target.value)}
            >
              {settings.map((s) => (
                <MenuItem key={s.settingId} value={s.settingId}>
                  {s.settingName}{s.active ? ' (Active)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={() => setNewProfileOpen(true)} disabled={isLoading}>
            New
          </Button>
        </Box>

        {/* Section 2 — Employee Entry Settings */}
        <Paper variant="outlined" sx={{ mb: 3, p: 2.5 }}>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>
            Employee Entry Settings
          </Typography>

          {/* Field List and Order */}
          <Paper variant="outlined">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle2">Field List and Order</Typography>
              <IconButton size="small" disabled>
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>

            {isLoading || loadingFields ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
            ) : fieldList.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                No fields configured. Select a profile or create one.
              </Typography>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={fieldList.map((f) => f.name)} strategy={verticalListSortingStrategy}>
                  {fieldList.map((field) => (
                    <DraggableRow
                      key={field.name}
                      field={field}
                      originalField={originalMap[field.name]}
                      onFieldChange={handleFieldChange}
                      onReset={handleItemReset}
                      onOpenValidation={handleOpenValidation}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </Paper>
        </Paper>

      </Box>

      {/* Section 3 — Full-width action bar pinned at bottom */}
      <Paper elevation={6} sx={{ position: 'sticky', bottom: 0, px: 3, py: 1.5, display: 'flex', gap: 1, borderRadius: 0, mt: 'auto' }}>
        <Button variant="contained" onClick={handleApply} disabled={saving || !selectedSettingId}>
          {saving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
          Apply
        </Button>
        <Button variant="outlined" onClick={handleReset} disabled={!selectedSettingId}>
          Reset
        </Button>
        <Button
          variant="outlined"
          color="error"
          onClick={() => setDeleteConfirmOpen(true)}
          disabled={!selectedSettingId || saving}
        >
          Delete
        </Button>
      </Paper>
      </Box>

      {/* Dialogs */}
      <NewProfileDialog
        open={newProfileOpen}
        onClose={() => setNewProfileOpen(false)}
        settings={settings}
        defaultCopyFromId={selectedSettingId}
        onCreated={async (setting, copyFromId) => {
          setSelectedSettingId(setting.settingId);
          try {
            // Copy field list from source profile if selected
            if (copyFromId) {
              const sourceFieldList = fieldLists[copyFromId] ?? [];
              if (sourceFieldList.length > 0) {
                await apiService.saveFieldList(setting.settingId, sourceFieldList);
              }
            }
            // Fetch the merged field list for the new profile
            const fieldRes = await apiService.getFieldList(setting.settingId);
            const fetched = fieldRes.data ?? [];
            // Persist with serials + activate — same as Apply button
            const withSerials = fetched.map((f: FieldSetting, idx: number) => ({ ...f, serial: idx + 1 }));
            const [, activateRes] = await Promise.all([
              apiService.saveFieldList(setting.settingId, withSerials),
              apiService.updateApplicationSetting(setting.settingId, { active: true }),
            ]);
            dispatch(setFieldListForSetting({ settingId: setting.settingId, fieldList: withSerials }));
            setFieldList(withSerials);
            // Reflect activation in Redux: new profile active, all others inactive
            dispatch(setSettings(
              [...settings, activateRes.data].map((s) =>
                s.settingId === setting.settingId ? activateRes.data : { ...s, active: false }
              )
            ));
          } catch {
            dispatch(addSetting(setting));
            dispatch(setFieldListForSetting({ settingId: setting.settingId, fieldList: [] }));
            setFieldList([]);
          }
        }}
      />
      <ValidationDialog
        open={validationOpen}
        field={validationField}
        onClose={() => setValidationOpen(false)}
        onSave={handleSaveValidation}
      />

      {/* Delete confirmation */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Profile</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Delete <strong>{settings.find((s) => s.settingId === selectedSettingId)?.settingName}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* [TEMP] UI Fields debug modal */}
      <Dialog open={uiFieldsOpen} onClose={() => setUiFieldsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>UI Fields — /application-settings/ui-fields</DialogTitle>
        <DialogContent dividers>
          {uiFieldsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              component="pre"
              sx={{ m: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
            >
              {JSON.stringify(uiFieldsData, null, 2)}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUiFieldsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <ImportEmployeeSettingsDialog
        open={importDialogOpen}
        fileName={importFileName}
        candidates={candidatesToImport}
        incompatible={incompatibleItems}
        importedCount={importedCount}
        status={importStatus}
        onClose={() => setImportDialogOpen(false)}
        onToggleSelect={handleToggleSelect}
        onToggleSelectAll={handleToggleSelectAll}
        onImport={handleExecuteImport}
      />
    </DashboardLayout>
  );
}
